import React, {useEffect} from "react"
import {useFormik} from "formik";
import * as yup from "yup";

import PriceCentField from "../../../components/PriceCentField";
import {useQuotes} from "../QuotesContext";
import ApartmentSelect from "./ApartmentSelect";

import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import Skeleton from "@mui/material/Skeleton";
import QuoteInfoBar from "../QuoteInfoBar";
import InputAdornment from "@mui/material/InputAdornment";

const keyGenerator = () => {
    let key = 0;

    return () => {
        key += 1;
        return key;
    }
}

const nextKey = keyGenerator();

export const useStepVariants = ({onSubmit = f => f}) => {
    const formik = useFormik({
            initialValues: {
                quoteVariants: [],
            },
            validationSchema: yup.object().shape({
                quoteVariants: yup.array().of(yup.object().shape({
                        quoteItems: yup.array().of(yup.object().shape({
                                apartmentId: yup.string()
                                    .min(1, 'Deve essere una struttura valida')
                                    .typeError('Deve essere una struttura valida')
                                    .required('Campo obbligatorio'),
                                priceCent: yup.number()
                                    .min(0, 'Deve essere un prezzo valido')
                                    .typeError('Deve essere un prezzo valido')
                                    .required('Campo obbligatorio'),
                            })
                        ).min(1, "Deve esserci almeno una struttura")
                    })
                ).min(1, "Deve esserci almeno un'alternativa")
            }),
            onSubmit: (values) => {
                const data = {
                    ...values,
                    quoteVariants: values.quoteVariants.map(({key, ...v}) => v),  // remove key attribute
                };
                onSubmit(data);
            },
        }
    );
    return {formik};
}

const StepVariants = (props) => {
    const {formik, variantsGenerator, petsNumber} = props;
    const {accommodations} = useQuotes();
    const hasMultipleApartments = accommodations.accommodationUnits.length > 1;

    const showAddVariantButton = hasMultipleApartments || formik.values.quoteVariants.length === 0

    const addEmptyVariant = () => {
        // add a key to the generated variant for react rendering
        formik.setFieldValue("quoteVariants", [
            ...formik.values.quoteVariants,
            {...variantsGenerator.emptyQuote, key: nextKey()}],
            false);
    }

    const deleteVariant = async (index) => {
        await formik.setFieldValue("quoteVariants", formik.values.quoteVariants.filter((q, i) => i !== index), false);
        formik.setErrors({});  // Delete all errors to avoid having errors on the deleted options
        formik.setTouched({});  // Delete all touches to avoid having touches on the deleted options
    }

    const addEmptyQuoteItem = (i) => {
        formik.setFieldValue(
            `quoteVariants[${i}].quoteItems`,
            [...formik.values.quoteVariants[i].quoteItems, variantsGenerator.emptyQuoteItem],
            false
        );
    }

    // Generate the initial variants as soon as the generator is loaded and enabled.
    // Once generated an option the generator is disabled to avoid generating options
    // when the user go back and forward through the multi steps that might replace
    // the user changes. The generator is re-enabled when the user change dates or
    // number of people or number of pets.
    useEffect(
        () => {
            if(!variantsGenerator.isLoading && variantsGenerator.enabled){
                formik.setFieldValue(
                    'quoteVariants',
                    variantsGenerator.generateVariants().map(v => ({...v, key: nextKey()})));
                variantsGenerator.setEnabled(false);
            }
        },
        [!variantsGenerator.isLoading, variantsGenerator.enabled]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Get the total price (sum of all quote items price) of the given variant
    const variantTotalPrice = (variant) => {
        return variant.quoteItems.reduce((acc, v) => acc + v.priceCent / 100.00, 0)
    }

    // Get the price per person per variant of the given variant
    const variantPersonNightPrice = (variant) => {
        const people = variantsGenerator.people;
        const nights = variantsGenerator.nights;
        if(nights > 0  && people > 0 ){
            return variantTotalPrice(variant) / people / nights;
        }else{
            return 0
        }
    }

    const handleSelectApartment = (event, variantIndex, itemIndex) => {
        const apartmentId = event.target.value;
        const quoteVariant = {
            ...formik.values.quoteVariants[variantIndex],
            quoteItems: formik.values.quoteVariants[variantIndex].quoteItems.filter((_, i) => i !== itemIndex)
        };
        const generatedPriceCent = variantsGenerator.generateApartmentPrice(apartmentId, quoteVariant);

        // Set the price, or set null if undefined
        formik.setFieldValue(`quoteVariants[${variantIndex}].quoteItems[${itemIndex}].priceCent`, generatedPriceCent || null);

        // Change the apartment selected
        formik.handleChange(event);
    }

    return <Stack spacing={3}>
        {
            (variantsGenerator.isLoading) ?
                <Stack flexGrow={1}>
                    <Skeleton height={40} width={200}/>
                    <Skeleton height={72}/>
                    <Skeleton height={40}/>
                </Stack>
                :
                <>
                    <QuoteInfoBar arrival={variantsGenerator.arrival}
                                  departure={variantsGenerator.departure}
                                  adults={variantsGenerator.adults}
                                  children={variantsGenerator.children}
                                  petsNumber={petsNumber}/>
                    {
                        formik.values.quoteVariants.length === 0 &&
                        <Stack spacing={1}>
                            <Typography variant='h6' color='primary' fontWeight="normal">
                                Strutture e prezzo
                            </Typography>
                            <Typography>
                                Nessuna alternativa presente per questo preventivo. Clicca sul pulsante sottostante per
                                aggiungerne una.
                            </Typography>

                            {
                                formik.touched.quoteVariants && formik.errors.quoteVariants && formik.values.quoteVariants.length === 0 &&
                                <Alert severity="error">
                                    Deve esserci almeno un'alternativa
                                </Alert>
                            }
                        </Stack>
                    }

                    {
                        formik.values.quoteVariants.map((quoteVariant, i) => (
                            <Card key={quoteVariant.key}
                                  sx={{
                                      p: "8px 16px",
                                      backgroundColor: "#fafafa",
                                      borderLeft: "4px solid",
                                      borderColor: "primary.main"
                                  }}>
                                <Typography variant="h6" color="primary" fontWeight="normal">
                                    {`Alternativa ${i + 1}`}
                                </Typography>

                                <List>
                                    {quoteVariant.quoteItems.map((quoteVariant, j) =>
                                        <ListItem key={j} disablePadding divider sx={{minHeight: 56}}>
                                            <ApartmentSelect bookingVariantsQuery={variantsGenerator.bookingVariantsQuery}
                                                             variant="standard"
                                                             name={`quoteVariants[${i}].quoteItems[${j}].apartmentId`}
                                                             value={formik.values.quoteVariants[i].quoteItems[j]?.apartmentId}
                                                             onChange={(event) => handleSelectApartment(event, i, j)}
                                                             error={formik.touched.quoteVariants?.[i]?.quoteItems?.[j]?.apartmentId && Boolean(formik.errors.quoteVariants?.[i]?.quoteItems?.[j]?.apartmentId)}
                                                             helperText={formik.touched.quoteVariants?.[i]?.quoteItems?.[j]?.apartmentId && formik.errors.quoteVariants?.[i]?.quoteItems?.[j]?.apartmentId}
                                                             SelectProps={{
                                                                 disableUnderline: true
                                                             }}/>
                                            <PriceCentField variant="standard"
                                                            label={null}
                                                            name={`quoteVariants[${i}].quoteItems[${j}].priceCent`}
                                                            onChange={(value) => formik.setFieldValue(`quoteVariants[${i}].quoteItems[${j}].priceCent`, value)}
                                                            value={formik.values.quoteVariants[i].quoteItems[j].priceCent}
                                                            error={formik.touched.quoteVariants?.[i]?.quoteItems?.[j]?.priceCent && Boolean(formik.errors.quoteVariants?.[i]?.quoteItems?.[j]?.priceCent)}
                                                            helperText={formik.touched.quoteVariants?.[i]?.quoteItems?.[j]?.priceCent && formik.errors.quoteVariants?.[i]?.quoteItems?.[j]?.priceCent}
                                                            sx={{border: 0}}
                                                            inputProps={{
                                                                sx: {textAlign: "right"},
                                                            }}
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                                                            }}/>
                                        </ListItem>
                                    )}
                                </List>

                                <Typography variant="h6" textAlign="right" color="success.main" fontWeight='regular'>
                                    Tot: {variantTotalPrice(quoteVariant).toFixed(2)} €
                                </Typography>
                                <Typography variant="caption" textAlign="right" component="div" fontWeight="light">
                                    ({variantPersonNightPrice(quoteVariant).toFixed(2)} € a notte per persona)
                                </Typography>

                                <Stack direction="row-reverse" justifyContent="space-between" flexGrow={1}>
                                    <IconButton onClick={() => deleteVariant(i)}>
                                        <DeleteIcon/>
                                    </IconButton>
                                    {
                                        hasMultipleApartments &&
                                        <Link component="button"
                                              underline="none"
                                              type="button"
                                              variant="body2"
                                              onClick={() => addEmptyQuoteItem(i)}
                                              sx={{mt: 1}}>
                                            Aggiungi struttura
                                        </Link>
                                    }
                                </Stack>

                            </Card>
                        ))
                    }

                    <Box textAlign="center">
                        <Button variant="contained"
                                color="secondary"
                                size='small'
                                sx={{visibility: showAddVariantButton ? "visible" : "hidden"}}
                                onClick={addEmptyVariant}>
                            Aggiungi alternativa al preventivo
                        </Button>
                    </Box>
                </>
        }
    </Stack>
}

export default StepVariants;